/**
 * The technician's top-bar pieces (FM Technician rebuild C1; INVENTORY
 * "Shell": sites, Online/Offline pill with data age and items to send, bell,
 * SOS, red Report incident).
 *
 * The sites pill is the existing SiteScopeChip and the bell the existing
 * NotificationCentre (AppLayout renders both). Here:
 *
 *   TechConnectionPill — "Online" / "Offline", a word and an icon on the
 *     colour; offline it says how old the screen's data is ("data from
 *     10:42 IST" on the site's clock, the last GET /tech/home that answered, D8); and how many items
 *     wait on this phone (the same pending queue the header badge reads —
 *     A16), opening the queue panel. It REPLACES PendingQueueBadge for the
 *     technician, so the count is not shown twice.
 *   TechIncidentButton — the red Report incident (C14, D11), from sm up; on a
 *     phone the same red item is in the menu, which keeps the bar inside 360.
 *
 * SOS (B41, D23) shipped with R3 safety: components/Tech/SosButton.tsx
 * (TechSos), mounted beside Report incident in AppLayout.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, SignalIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import PendingQueuePanel from '@/components/Common/PendingQueuePanel';
import { usePendingQueue } from '@/components/Common/usePendingQueue';
import { canOfferInNav, canOpenRoute } from '@/config/routeAccess';
import { TECH_INCIDENT } from '@/config/techExperience';
import { useAuthStore } from '@/stores/authStore';
import { fmTime } from '@/utils/fmTime';
import { useTechZone } from '@/components/Tech/techZone';

/** navigator.onLine, kept current by the browser's online/offline events. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function TechConnectionPill({ dataUpdatedAt }: { dataUpdatedAt: number }) {
  const online = useOnline();
  const { waiting, blocked } = usePendingQueue();
  const [open, setOpen] = useState(false);
  const unsent = waiting.length + blocked.length;
  // The site's clock, named ("10:42 IST") — review R2-1.
  const zone = useTechZone();
  const dataFrom = dataUpdatedAt > 0 ? fmTime(new Date(dataUpdatedAt), zone) : null;
  const Icon = online ? SignalIcon : SignalSlashIcon;
  const label = [
    online ? 'Online' : 'Offline',
    !online && dataFrom ? `data from ${dataFrom}` : null,
    unsent > 0 ? `${unsent} ${unsent === 1 ? 'item' : 'items'} to send` : null,
  ].filter(Boolean).join(', ');
  // C22: once the Sync page ships, the pill opens it (the prototype's pill
  // goes to Sync); until then, the R1 panel.
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const toSync = canOpenRoute(user, '/tech/sync');
  return (
    <>
      <button
        type="button"
        onClick={() => (toSync ? navigate('/tech/sync') : setOpen(true))}
        data-testid="tech-connection-pill"
        data-online={online ? 'yes' : 'no'}
        aria-label={`${label}. Open what is waiting on this phone.`}
        title={label}
        className={`inline-flex min-h-10 min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap rounded-full border px-2.5 text-sm font-medium ${
          online && blocked.length === 0
            ? 'border-success-200 bg-success-50 text-success-700'
            : 'border-warning-200 bg-warning-50 text-warning-800'}`}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {/* The pill gives way first when the bar is tight (360 px): the
            words truncate, the icon and the count stay. */}
        <span className="min-w-0 truncate" data-testid="tech-pill-words">
          {online ? 'Online' : 'Offline'}
          {!online && dataFrom && (
            <span className="font-mono tabular-nums" data-testid="tech-data-age">
              <span className="hidden sm:inline"> · data from </span>
              <span className="sm:hidden"> · </span>{dataFrom}
            </span>
          )}
        </span>
        {unsent > 0 && (
          <span className="shrink-0 rounded-full bg-surface px-1.5 font-semibold" data-testid="tech-unsent">
            {unsent}<span className="hidden sm:inline"> to send</span>
          </span>
        )}
      </button>
      <PendingQueuePanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function TechIncidentButton() {
  const user = useAuthStore((s) => s.user);
  if (!canOfferInNav(user, TECH_INCIDENT)) return null;
  return (
    <Link
      to={TECH_INCIDENT}
      data-testid="topbar-report-incident"
      className="hidden min-h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-danger-600 px-3 text-sm font-semibold text-white hover:bg-danger-700 sm:inline-flex"
    >
      <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
      Report incident
    </Link>
  );
}
